import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { useAuth } from '../AuthContext';
import { useAxios } from '../core/_axios';
import { 
  Tablet, 
  PenTool, 
  Monitor,
  Check,
  ExternalLink,
  Book
} from 'lucide-react';

interface SupplyItem {
  id: string;
  category: 'essential' | 'optional';
  name: string;
  description: string;
  price: string;
  features: string[];
  amazonUrl: string;
  recommended?: boolean;
}

const Supplies: React.FC = () => {
  usePageTitle('Recommended Supplies');
  const { setPageHeader } = usePageHeader();
  const { currentUser } = useAuth();
  const axios = useAxios();
  
  useEffect(() => {
    const headerContent = (
      <Link to="/welcome" className="btn btn-sm btn-light">
        Back to Dashboard
      </Link>
    );
    
    setPageHeader('Recommended Supplies', headerContent);
    
    // Mark supplies as viewed in user data
    if (currentUser && !currentUser.public_data?.supplies_viewed) {
      console.log('[Supplies] Marking supplies as viewed...');
      const markSuppliesViewed = async () => {
        try {
          const response = await axios.put('/api/v1/app/authenticated/update-user-data', {
            supplies_viewed: true
          });
          console.log('[Supplies] Successfully marked supplies as viewed');
          
          // Update the currentUser object to reflect the change immediately
          if (currentUser) {
            currentUser.public_data = {
              ...currentUser.public_data,
              supplies_viewed: true
            };
          }
        } catch (error) {
          console.error('[Supplies] Failed to mark supplies as viewed:', error);
        }
      };
      markSuppliesViewed();
    } else {
      console.log('[Supplies] Supplies already marked as viewed or no user');
    }
    
    return () => setPageHeader(null);
  }, [setPageHeader, currentUser, axios]);

  const essentials: SupplyItem[] = [
    {
      id: 'ipad-10th-gen',
      category: 'essential',
      name: 'iPad',
      description: '10.9-inch Wi-Fi 256GB - Best value for funeral homes',
      price: '$349',
      features: [
        '10.9-inch display',
        'All-day battery life',
        'USB-C charging',
        '256GB storage'
      ],
      amazonUrl: 'https://www.amazon.com/Apple-2022-10-9-inch-Wi-Fi-256GB/dp/B0BJLXMVMW/'
    },
    {
      id: 'stylus-pen',
      category: 'essential',
      name: 'Stylus Pen',
      description: 'Professional dual-tip stylus - works great, costs less',
      price: '$12.99',
      features: [
        'No pairing needed',
        'Palm rejection',
        '20-hour battery',
        'Dual tip design'
      ],
      amazonUrl: 'https://www.amazon.com/Dual-Tip-Generation-Rejection-Sensitivity-Compatible/dp/B0CKYLV6RC/',
      recommended: true
    }
  ];

  const optionalStands: SupplyItem[] = [
    {
      id: 'desktop-stand',
      category: 'optional',
      name: 'Desktop Stand',
      description: 'For registration desks',
      price: '$10+',
      features: [
        'Weighted base',
        'Adjustable angle',
        '360° rotation',
        'Stable & professional'
      ],
      amazonUrl: 'https://www.amazon.com/s?k=ipad+desktop+stand+adjustable+weighted'
    },
    {
      id: 'floor-stand',
      category: 'optional',
      name: 'Floor Stand',
      description: 'For entrances',
      price: '$60+',
      features: [
        'Height adjustable',
        'Freestanding',
        'Cable management',
        'Professional look'
      ],
      amazonUrl: 'https://www.amazon.com/s?k=ipad+floor+stand+adjustable+height'
    }
  ];

  const totalCost = '$362';

  return (
    <div className="container-fixed">
      <div className="flex flex-col gap-6 py-6">
        
        {/* What You Need Section */}
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold mb-3">Everything You Need</h2>
          <p className="text-gray-600">
            Just two essential items to get started. Total cost: <span className="font-bold text-primary">{totalCost}</span>
          </p>
        </div>

        {/* Essential Items */}
        <div>
          <h3 className="text-lg font-semibold mb-4 text-gray-700">Essential Items</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {essentials.map((item) => (
              <div key={item.id} className="card">
                <div className="card-body p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="font-bold text-lg">{item.name}</h4>
                      <p className="text-sm text-gray-600">{item.description}</p>
                    </div>
                    <div className="text-2xl font-bold text-primary">{item.price}</div>
                  </div>
                  
                  <ul className="space-y-2 mb-6">
                    {item.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-success flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <a
                    href={item.amazonUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`btn ${item.recommended ? 'btn-primary' : 'btn-light'} btn-sm w-full`}
                  >
                    {item.recommended && 'Recommended: '}View on Amazon
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Optional Stands */}
        <div>
          <h3 className="text-lg font-semibold mb-4 text-gray-700">Optional: iPad Stands</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {optionalStands.map((item) => (
              <div key={item.id} className="card">
                <div className="card-body p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="font-bold text-lg">{item.name}</h4>
                      <p className="text-sm text-gray-600">{item.description}</p>
                    </div>
                    <div className="text-2xl font-bold text-primary">{item.price}</div>
                  </div>
                  
                  <ul className="space-y-2 mb-6">
                    {item.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-success flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <a
                    href={item.amazonUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-light btn-sm w-full"
                  >
                    View Options on Amazon
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Why These Items Section */}
        <div className="card bg-gray-50">
          <div className="card-body p-6">
            <h3 className="text-lg font-semibold mb-4">Why We Recommend These</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-primary mb-2">Skip the Apple Pencil</h4>
                <p className="text-sm text-gray-700">
                  Apple Pencils are expensive ($99+) and confusing with multiple versions. 
                  The stylus we recommend works just as well for signatures at a fraction of the cost.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-primary mb-2">Flexible Signature Options</h4>
                <p className="text-sm text-gray-700">
                  QR code signatures are quick and easy for remote guests. For in-person signing with iPads, 
                  use stands for stationary setups. Note: If QR code signatures are all you want, 
                  you don't need to buy iPads or styluses.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="card bg-gradient-to-r from-info-light to-info/10">
          <div className="card-body p-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold mb-2">Questions about setup?</h3>
                <p className="text-gray-600 mb-4">
                  Our team can help you get started quickly with your new equipment.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => window.open('https://link.mogulclients.com/widget/appointment/mogulclients?user_id=Dpu2AeqF9Rh1n3eDjVdU', '_blank')}
                    className="btn btn-primary"
                  >
                    Schedule Setup Call
                  </button>
                </div>
              </div>
              <Book className="w-24 h-24 text-info opacity-20" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Supplies;